import { DISTRICTS } from "../data/city";
import { GameState } from "../systems/GameState";
import { riskIcons, DISTRICT_RISKS, RISK_ICON } from "./prl";

/**
 * Panel derecho: CENTRAL DE VILLA SEGURA. Sala de control de la unidad EPI:
 * nivel de alerta, indicadores, riesgos activos, ciudadanos protegidos y un
 * mensaje de central que despacha a EPI al siguiente sector. Estética de panel
 * industrial, a juego con el Centro de Operaciones de la izquierda ([[HubMenu]]).
 */

const ANTENNA = `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M12 14v7" stroke="#f0c400" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="12" r="2.4" fill="#f0c400"/><path d="M7.5 7.5a6 6 0 0 0 0 9M16.5 7.5a6 6 0 0 1 0 9M4.5 4.5a10 10 0 0 0 0 15M19.5 4.5a10 10 0 0 1 0 15" fill="none" stroke="#8a785a" stroke-width="1.6" stroke-linecap="round"/></svg>`;

/** Habitantes nominales por sector (para "ciudadanos protegidos"). */
const POP: Record<string, number> = {
  obras: 420, fabrica: 680, labo: 240, torre: 1150, gris: 900, central: 300,
  poligono: 560, bomberos: 180, hospital: 1400, hotel: 520,
};

export class TownPanel {
  private root: HTMLElement;
  private alertEl!: HTMLElement;
  private gridEl!: HTMLElement;
  private risksEl!: HTMLElement;
  private lastEl!: HTMLElement;
  private msgEl!: HTMLElement;
  private sig = "";
  private timer: number;

  constructor() {
    injectTownStyles();
    this.root = document.getElementById("sidepanel") ?? document.body;
    this.build();
    this.refresh();
    this.timer = window.setInterval(() => this.refresh(), 700);
  }

  private build(): void {
    this.root.innerHTML = "";

    const head = div("cv-head");
    head.innerHTML =
      `<div class="cv-antenna">${ANTENNA}</div>` +
      `<div class="cv-head-t"><b>Central de Villa Segura</b><span>Sala de control · Unidad EPI</span></div>` +
      `<span class="cv-live"><i></i>EN LÍNEA</span>`;
    this.root.appendChild(head);
    this.root.appendChild(tape());

    this.alertEl = div("cv-alert");
    this.root.appendChild(this.alertEl);

    this.root.appendChild(plate("Indicadores"));
    this.gridEl = div("cv-grid");
    this.root.appendChild(this.gridEl);

    this.root.appendChild(plate("Riesgos activos"));
    this.risksEl = div("cv-risks");
    this.root.appendChild(this.risksEl);

    this.root.appendChild(plate("Última intervención"));
    this.lastEl = div("cv-last");
    this.root.appendChild(this.lastEl);

    this.root.appendChild(plate("Mensaje de central"));
    this.msgEl = div("cv-msg");
    this.root.appendChild(this.msgEl);
  }

  refresh(): void {
    const st = new GameState();
    const playable = DISTRICTS.filter((d) => d.sceneKey);
    const resolved = playable.filter((d) => st.chapter(d.id).done);
    const front = playable.filter((d) => !st.chapter(d.id).done && (!d.requires || st.chapter(d.requires).done));
    const next = front[0];
    const secs = resolved.map((d) => st.security[d.id]).filter((s): s is number => typeof s === "number");
    const avg = secs.length ? Math.round(secs.reduce((a, b) => a + b, 0) / secs.length) : 0;
    const citizens = resolved.reduce((a, d) => a + (POP[d.id] ?? 0), 0);

    // riesgos activos: unión de los sectores del frente (aún sin asegurar)
    const activeRisks = Array.from(new Set(front.flatMap((d) => DISTRICT_RISKS[d.id] ?? [])));

    const sig = `${resolved.length}#${avg}#${next?.id ?? "-"}#${activeRisks.join(",")}`;
    if (sig === this.sig) return;
    this.sig = sig;

    // nivel de alerta
    let level: { txt: string; cls: string; seg: number };
    if (resolved.length === playable.length) level = { txt: "SECTORES ASEGURADOS", cls: "cv-verde", seg: 1 };
    else if (resolved.length === 0) level = { txt: "ALERTA ALTA", cls: "cv-rojo", seg: 3 };
    else if (avg >= 75) level = { txt: "ALERTA CONTROLADA", cls: "cv-ambar", seg: 2 };
    else level = { txt: "ALERTA ELEVADA", cls: "cv-rojo", seg: 3 };

    this.alertEl.className = "cv-alert " + level.cls;
    this.alertEl.innerHTML =
      `<span class="cv-alert-k">Nivel de alerta</span>` +
      `<span class="cv-alert-v">${level.txt}</span>` +
      `<span class="cv-alert-leds">${[1, 2, 3].map((n) => `<i class="${n <= level.seg ? "on" : ""}"></i>`).join("")}</span>`;

    this.gridEl.innerHTML =
      cell("Sectores protegidos", `${resolved.length}/${playable.length}`) +
      cell("Seguridad media", `${avg}%`) +
      cell("Ciudadanos protegidos", citizens.toLocaleString("es-ES")) +
      cell("Frentes abiertos", `${front.length}`);

    this.risksEl.innerHTML = activeRisks.length
      ? activeRisks.map((k) => `<span class="cv-risk" title="${k}">${RISK_ICON[k] ?? ""}</span>`).join("") +
        `<span class="cv-risk-n">${activeRisks.length} tipos en vigilancia</span>`
      : `<span class="cv-clear">Sin riesgos activos. Villa Segura despejada.</span>`;

    const last = resolved[resolved.length - 1];
    this.lastEl.innerHTML = last
      ? `<span class="cv-last-name">${last.name}</span><span class="cv-last-sec">${st.security[last.id] ?? 0}% asegurado</span>${riskIcons(last.id)}`
      : `<span class="cv-last-none">Aún no hay intervenciones registradas.</span>`;

    const msg = next
      ? `EPI, se han detectado incidencias en <b>${next.name}</b>. Acude a asegurar el sector.`
      : `Todos los sectores asegurados. Buen trabajo, inspector. Villa Segura hace honor a su nombre.`;
    this.msgEl.innerHTML = `<span class="cv-msg-tag">CENTRAL ▸ EPI</span><p>${msg}</p>`;
  }

  destroy(): void {
    window.clearInterval(this.timer);
  }
}

function div(cls: string): HTMLElement { const n = document.createElement("div"); n.className = cls; return n; }
function plate(text: string): HTMLElement { const n = div("cv-plate"); n.textContent = text; return n; }
function tape(): HTMLElement { return div("cv-tape"); }
function cell(label: string, value: string): string {
  return `<div class="cv-cell"><span>${label}</span><b>${value}</b></div>`;
}

function injectTownStyles(): void {
  if (document.getElementById("cv-panel-styles")) return;
  const tapeBg = "repeating-linear-gradient(45deg,#f0c400 0 11px,#171308 11px 22px)";
  const css = `
  #sidepanel{font-family:"Segoe UI",system-ui,sans-serif;color:#e7dcc4;padding:0 0 20px;box-sizing:border-box;display:flex;flex-direction:column;gap:10px;background:#14120c;background-image:linear-gradient(#14120c,#14120c),radial-gradient(rgba(255,255,255,.02) 1px,transparent 1px);background-size:auto,6px 6px}
  #sidepanel::-webkit-scrollbar{width:9px}#sidepanel::-webkit-scrollbar-thumb{background:#3a2f1c}
  .cv-head{display:flex;align-items:center;gap:9px;background:linear-gradient(#22201a,#1a1710);border-bottom:1px solid #3a2f1c;padding:12px 12px 11px}
  .cv-antenna{width:34px;height:34px;flex:0 0 auto;background:#14120c;border:2px solid #3a2f1c;display:flex;align-items:center;justify-content:center;box-shadow:inset 0 0 0 1px #000}
  .cv-head-t{display:flex;flex-direction:column;line-height:1.12;flex:1;min-width:0}
  .cv-head-t b{font-size:13px;color:#f4ead0;letter-spacing:.02em;font-weight:800}
  .cv-head-t span{font-size:9.5px;color:#9c8b6c;text-transform:uppercase;letter-spacing:.06em;margin-top:2px}
  .cv-live{display:flex;align-items:center;gap:4px;font-size:8px;font-weight:800;letter-spacing:.06em;color:#7fe0a6}
  .cv-live>i{width:7px;height:7px;background:#2f8f57;border-radius:50%;box-shadow:0 0 0 0 rgba(47,143,87,.6);animation:cvpulse 1.8s infinite}
  @keyframes cvpulse{0%{box-shadow:0 0 0 0 rgba(47,143,87,.6)}70%{box-shadow:0 0 0 6px rgba(47,143,87,0)}100%{box-shadow:0 0 0 0 rgba(47,143,87,0)}}
  @media(prefers-reduced-motion:reduce){.cv-live>i{animation:none}}
  .cv-tape{height:8px;background:${tapeBg};opacity:.85}
  .cv-plate{margin:6px 12px 0;font-size:10px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:#171308;background:#f0c400;padding:3px 8px;box-shadow:2px 2px 0 #000;align-self:flex-start;max-width:calc(100% - 24px)}
  /* nivel de alerta */
  .cv-alert{margin:2px 12px 0;border:1px solid #3a2f1c;border-left-width:6px;background:#1c1810;padding:11px 12px;display:grid;grid-template-columns:1fr auto;align-items:center;gap:2px 8px}
  .cv-alert-k{font-size:9.5px;color:#9c8b6c;text-transform:uppercase;letter-spacing:.06em}
  .cv-alert-v{font-size:16px;font-weight:800;letter-spacing:.02em;grid-column:1}
  .cv-alert-leds{grid-row:1 / span 2;display:flex;flex-direction:column;gap:4px}
  .cv-alert-leds>i{width:14px;height:8px;background:#0d0a06;border:1px solid #3a2f1c}
  .cv-rojo{border-left-color:#c4122e}.cv-rojo .cv-alert-v{color:#f0a08f}.cv-rojo .cv-alert-leds>i.on{background:#c4122e;box-shadow:0 0 6px #c4122e}
  .cv-ambar{border-left-color:#f0a91b}.cv-ambar .cv-alert-v{color:#ffd489}.cv-ambar .cv-alert-leds>i.on{background:#f0a91b;box-shadow:0 0 6px #f0a91b}
  .cv-verde{border-left-color:#2f8f57}.cv-verde .cv-alert-v{color:#7fe0a6}.cv-verde .cv-alert-leds>i.on{background:#2f8f57;box-shadow:0 0 6px #2f8f57}
  /* indicadores */
  .cv-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px;padding:0 12px}
  .cv-cell{background:#1c1810;border:1px solid #2c2114;border-top:2px solid #3a2f1c;padding:8px 9px}
  .cv-cell span{display:block;font-size:8.5px;color:#9c8b6c;text-transform:uppercase;letter-spacing:.03em;line-height:1.25;min-height:2.3em}
  .cv-cell b{font-size:17px;color:#f4ead0;font-variant-numeric:tabular-nums;letter-spacing:.01em}
  /* riesgos activos */
  .cv-risks{display:flex;align-items:center;gap:5px;flex-wrap:wrap;padding:0 12px}
  .cv-risk{width:26px;height:26px;background:#1c1810;border:1px solid #2c2114;display:flex;align-items:center;justify-content:center}
  .cv-risk-n{font-size:9.5px;color:#9c8b6c;text-transform:uppercase;letter-spacing:.04em;margin-left:2px}
  .cv-clear{font-size:11.5px;color:#7fe0a6;padding:2px 0}
  /* última intervención */
  .cv-last{display:flex;align-items:center;gap:6px;flex-wrap:wrap;padding:0 12px}
  .cv-last-name{font-size:12.5px;font-weight:700;color:#f4ead0}
  .cv-last-sec{font-size:9.5px;color:#7fe0a6;border:1px solid #2f8f57;padding:1px 5px}
  .cv-last .op-risk,.cv-last .cv-risk{width:20px;height:20px}
  .cv-last-none{font-size:11.5px;color:#9c8b6c}
  /* mensaje de central (télex) */
  .cv-msg{margin:0 12px;background:#0f1a14;border:1px solid #1e3a2a;border-left:4px solid #2f8f57;padding:10px 11px}
  .cv-msg-tag{display:inline-block;font-size:8.5px;font-weight:800;letter-spacing:.08em;color:#7fe0a6;margin-bottom:5px}
  .cv-msg p{margin:0;font-size:12px;line-height:1.5;color:#cdeede;font-family:"Consolas","Courier New",monospace}
  .cv-msg b{color:#fff;font-weight:800}
  `;
  const style = document.createElement("style");
  style.id = "cv-panel-styles";
  style.textContent = css;
  document.head.appendChild(style);
}
